import { useContext } from "react";
import {
  BatchInitGenerateQuestionsDataInput,
  CompetencyMapQuery,
  Grade,
  Language,
  Question_Type,
  QuestionSubtypesQuery,
  useBatchInitGenerateQuestionsMutation,
  useQuestionSubtypesQuery,
} from "@generated/graphql";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Stack,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useForm } from "react-hook-form";
import Form from "components/form/Form";
import TextFieldControl from "components/form/TextFieldControl";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { LoadingButton } from "@mui/lab";
import AutocompleteControl from "components/form/AutocompleteControl";
import { GENERATE_QUESTIONS_DEFAULT_AMOUNT, GRADE_OPTIONS, QUESTION_TYPE_TITLE_ADJECTIVE } from "constants/global";
import SelectControl from "components/form/SelectControl";
import { QUESTION_LANGUAGE_OPTIONS } from "constants/language";
import { parseApolloError } from "helpers/errorsParser";
import CheckboxControl from "components/form/CheckboxControl";
import CircularLoading from "components/CircularLoading";
import { RefetchCompetencyMapGenQuestionsLog } from "pages/CompetencyMaps/view/components/types";

type QuestionsSubtypeValue = Record<string, BatchInitGenerateQuestionsDataInput>;

const getQuestionSubtypesData = (subtypesGroups: QuestionSubtypesQuery["questionSubtypes"]): QuestionsSubtypeValue => {
  return subtypesGroups.reduce(
    (subtypesGroupsAcc, subtypeGroup) => ({
      ...subtypesGroupsAcc,
      ...(subtypeGroup.subtypes.length
        ? subtypeGroup.subtypes.reduce(
            (subtypesAcc, subtype, index) => ({
              ...subtypesAcc,
              [`${subtypeGroup.type}_${index}`]: {
                type: subtypeGroup.type,
                subtype,
              },
            }),
            {}
          )
        : { [subtypeGroup.type]: { type: subtypeGroup.type, subtype: undefined } }),
    }),
    {}
  );
};

const getQuestionSubtypesOptions = (data: QuestionsSubtypeValue) => {
  return Object.entries(data).map(([key, value]) => ({
    label: `${QUESTION_TYPE_TITLE_ADJECTIVE[value.type]}${value.subtype ? `: ${value.subtype}` : ""}`,
    value: key,
  }));
};

export type GenerateQuestionsForCMModalProps = {
  competencyMap: CompetencyMapQuery["competencyMap"];
  open: boolean;
  onClose: () => void;
  refetchCompetencyMapGenQuestionsLog: RefetchCompetencyMapGenQuestionsLog;
};

export type GenerateQuestionsForCMModalForm = {
  grade: { id: Grade; name: string } | null;
  questionSubtypes: Record<string, boolean>;
  forDisplaySubtypesErrors: null;
  language: Language;
  amount: string;
};

const GenerateQuestionsForCMModal = ({
  competencyMap,
  open,
  onClose,
  refetchCompetencyMapGenQuestionsLog,
}: GenerateQuestionsForCMModalProps) => {
  const { addToast } = useContext(ToastContext);
  const [generateQuestions, { loading }] = useBatchInitGenerateQuestionsMutation();
  const { data, loading: questionSubtypesLoading } = useQuestionSubtypesQuery({
    variables: {
      questionType: [Question_Type.Practical, Question_Type.Theoretical, Question_Type.MultipleChoiceQuestions],
    },
  });

  const questionSubtypesData = data?.questionSubtypes ? getQuestionSubtypesData(data.questionSubtypes) : {};

  const questionSubtypes = questionSubtypesData ? getQuestionSubtypesOptions(questionSubtypesData) : [];

  const form = useForm<GenerateQuestionsForCMModalForm>({
    defaultValues: {
      grade: null,
      questionSubtypes: {},
      language: Language.Ru,
      amount: String(GENERATE_QUESTIONS_DEFAULT_AMOUNT),
    },
  });

  const {
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    getValues,
    formState: { errors },
  } = form;

  const formValues = getValues();

  const validateQuestionSubtypesInputs = (formData: GenerateQuestionsForCMModalForm) => {
    const checkedQuestionSubtypesEntires = Object.entries(formData.questionSubtypes).filter(([, value]) => value);

    if (checkedQuestionSubtypesEntires.length === 0 || checkedQuestionSubtypesEntires.length > 3) {
      setError("forDisplaySubtypesErrors", {
        type: "required",
        message: "Необходимо выбрать от 1 до 3 типов вопросов",
      });
      return null;
    }

    clearErrors("forDisplaySubtypesErrors");

    return checkedQuestionSubtypesEntires;
  };

  const onSubmit = (formData: GenerateQuestionsForCMModalForm) => {
    if (!competencyMap?.id || !questionSubtypesData) return;

    const checkedQuestionSubtypesEntires = validateQuestionSubtypesInputs(formData);

    if (!checkedQuestionSubtypesEntires) return;

    generateQuestions({
      variables: {
        input: {
          competencyMapId: competencyMap.id,
          // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          grade: formData.grade?.id!,
          data: checkedQuestionSubtypesEntires.map(([key]) => questionSubtypesData[key]),
          language: formData.language,
          amount: Number(formData.amount),
        },
      },
    })
      .then(() => {
        addToast({ type: ToastTypeEnum.SUCCESS, text: "Генерация вопросов может занимать несколько минут" });
        refetchCompetencyMapGenQuestionsLog();
        onClose();
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  const onChangeAmount = (value: number) => {
    let resValue = String(value);

    if (value < 1) resValue = "1";
    else if (value > 3) resValue = "3";

    setValue("amount", resValue);
  };

  const subtypesErrors = errors.forDisplaySubtypesErrors;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='sm'>
      {questionSubtypesLoading ? (
        <CircularLoading />
      ) : (
        <>
          <DialogTitle
            variant='h24'
            textAlign='center'>
            Генерация вопросов
          </DialogTitle>
          <DialogContent sx={{ mb: 3 }}>
            <Form form={form}>
              <Stack spacing={2}>
                <AutocompleteControl
                  options={GRADE_OPTIONS}
                  name='grade'
                  label='Грейд'
                  rules={{
                    required: true,
                  }}
                />
                <Stack spacing={0.5}>
                  <Typography
                    variant='body14rg'
                    sx={{ color: "base.500" }}>
                    Типы вопросов
                  </Typography>
                  <FormControl error={Boolean(subtypesErrors)}>
                    <Stack>
                      {questionSubtypes.map((subtype) => (
                        <CheckboxControl
                          key={subtype.value}
                          name={`questionSubtypes.${subtype.value}`}
                          label={subtype.label}
                          sx={{ margin: 0 }}
                          handleChange={(value) =>
                            validateQuestionSubtypesInputs({
                              ...formValues,
                              questionSubtypes: {
                                ...formValues.questionSubtypes,
                                [subtype.value]: value,
                              },
                            })
                          }
                        />
                      ))}
                    </Stack>
                    {subtypesErrors?.message && <FormHelperText>{subtypesErrors?.message}</FormHelperText>}
                  </FormControl>
                </Stack>
                <SelectControl
                  name='language'
                  items={QUESTION_LANGUAGE_OPTIONS}
                  sx={{ backgroundColor: "bgSwitch" }}
                  label='Язык вопроса'
                  rules={{
                    required: true,
                  }}
                />
                <TextFieldControl
                  name='amount'
                  type='number'
                  onNumberValueChange={onChangeAmount}
                  label='Количество вопросов'
                  rules={{
                    required: true,
                    min: {
                      value: 1,
                      message: "Минимальное количество вопросов для генерации - 1",
                    },
                    max: {
                      value: 3,
                      message: "Максимальное количество вопросов для генерации - 3",
                    },
                  }}
                />
              </Stack>
            </Form>
          </DialogContent>
          <DialogActions>
            <Button
              variant='outlined'
              onClick={onClose}>
              Отмена
            </Button>
            <LoadingButton
              variant='contained'
              onClick={handleSubmit(onSubmit)}
              loading={loading}>
              Сгенерировать
            </LoadingButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default GenerateQuestionsForCMModal;
